/* eslint-disable */
// @ts-nocheck
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { isNativeApp } from '../../../../client'
import { useGridBlock, usePage, usePageVisibility } from '../../../../contexts'
import { prettyLog, sendGA4, t_event_name } from '../../../../utils'
import {
  default_player_progress_report_map,
  PLAYER_EVENT,
  playerConfig,
  useEventListener,
} from '../../../Player/Helpers'
import { PlayerDiv } from './Player.style'
import { usePlayer } from '../PlayerContext'
import { getVerticalPlayerConfig } from '../utils'
import { PlayerOverlay } from './PlayerOverlay'
import { useSwiper } from 'swiper/react'
import { IPlayer } from '../type'
import { useRootStore } from '../../../../store'

/** a slide passed in a fast swipe gets no player (the look-ahead needs no delay: it starts after the active clip plays) */
const ACTIVE_SETUP_DELAY_MS = 150
/**
 * Video buffered ahead before the feed loads the neighbours, so they only use
 * bandwidth this clip does not need. On a fast link it is there ~0.2 s after
 * the start; on 3g, where the clip alone fills the link, the neighbours wait
 * (measured over 30 s: 2.9 s of stalls instead of 8.4 s, at the cost of a
 * slower swipe there).
 */
const NEIGHBOURS_AFTER_BUFFER_S = 4

// The video's own frame at 0s, from Kaltura's thumbnail service
const kalturaFirstFrameUrl = (kalturaId: string) =>
  `https://cfvod.kaltura.com/p/2748741/sp/274874100/thumbnail/entry_id/${kalturaId}/width/360/vid_sec/0`

const safeDestroyKp = (instance: { destroy?: () => void } | null) => {
  if (!instance?.destroy) {
    return
  }
  try {
    instance.destroy()
  } catch (e) {
    prettyLog('player destroy', { e }, 'error')
  }
}
/** how often a playing clip reports its time to `onTime` */
const TIME_REPORT_MS = 250
/** Kaltura error 3018: "the adapter has been destroyed while loading" */
const KALTURA_DESTROYED_WHILE_LOADING = 3018
/** Playkit error category 7 = PLAYER */
const KALTURA_PLAYER_CATEGORY = 7

// A player destroyed while its adapter loads (the user left the slide) makes
// Kaltura reject a promise inside its own code, one nothing of ours can catch.
// Only that rejection is marked handled; every other one still surfaces.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason as { code?: number; category?: number } | null
    if (
      reason?.code === KALTURA_DESTROYED_WHILE_LOADING &&
      reason?.category === KALTURA_PLAYER_CATEGORY
    ) {
      event.preventDefault()
    }
  })
}

/**
 * play() rejects when the player is destroyed while it loads (Kaltura 3018,
 * "the adapter has been destroyed while loading"): the user already left
 * that clip, so it is not an error
 */
const playQuietly = (instance: { play?: () => unknown } | null) => {
  try {
    const result = instance?.play?.() as { catch?: (f: () => void) => void }
    result?.catch?.(() => undefined)
  } catch (e) {
    prettyLog('player play', { e }, 'error')
  }
}

export const Player = ({
  slidedData,
  index,
  isVisible,
  handleClosePlayer,
  seekBarColor = '#fff',
  isActive,
  playerStyle = {
    borderCorner: 'square',
    seekBar: 'linear',
    overlayPadding: 20,
    overlayPaddingMobile: 12,
    seekBarTimeSize: 16,
    overlayInfoPaddingBottom: 30,
    overlayInfoPaddingBottomMobile: 16,
    overlayInfoGap: 14,
  },
  eventPrefix = 'vertical_',
  appEventPrefix = true,
  adTagUrl,
  loop,
  playerIdOverride,
  shouldLoad,
  onPlaying,
  onStart,
  slideKey,
  showPoster = true,
  onEnded,
  startAt = 0,
  onTime,
  hideOverlay = false,
  paused = false,
  analyticsParams,
  restartKey,
  seekTo,
  onError,
  keepPlace = false,
  onLoading,
}: IPlayer) => {
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError
  // read from player events and timers, so kept in refs
  const onEndedRef = useRef(onEnded)
  onEndedRef.current = onEnded
  const onTimeRef = useRef(onTime)
  onTimeRef.current = onTime
  const pausedRef = useRef(paused)
  pausedRef.current = paused
  // used once, by the first load: a later visit starts over
  const startAtRef = useRef(startAt)
  const {
    video,
    realIndex,
    episode,
    img,
    title,
    link,
    link_title,
    timelineItemTime,
    timelineItemDay,
  } = slidedData
  const swiper = useSwiper()
  const { gridId, index: blockIndex } = useGridBlock()
  const kpSetKpTO = useRef<NodeJS.Timeout | null>(null)
  const hasEndedRef = useRef(false)
  /** this load played (Kaltura's PLAYBACK_START, once per load) */
  const playbackStartedRef = useRef(false)
  const kpRef = useRef<any | null>(null)
  const [kp, setKp] = useState<any | null>(null)
  const playerId = useMemo(
    () =>
      playerIdOverride ||
      `vertical-player-${
        slideKey ?? `s${index}`
      }-gid${gridId}-gindex${blockIndex}`,
    [blockIndex, gridId, index, playerIdOverride, slideKey]
  )
  const [progress_report_map, setProgressReportMap] = useState([
    ...default_player_progress_report_map,
  ])
  const [firstPlay, setFirstPlay] = useState(false)
  const [isNextVideoTip, setIsNextVideoTip] = useState(false)
  const [afterPageIsNotVisible, setAfterPageIsNotVisible] = useState(false)
  const [wasPaused, setWasPaused] = useState(true)
  const [isPaused, setIsPaused] = useState(true)
  const [isAdPlaying, setIsAdPlaying] = useState(false)
  const [titleHeader, setTitleHeader] = useState('')
  const isApp = useMemo(() => isNativeApp(), [])

  // Kaltura draws nothing until the video has a frame, so these layers are
  // what the slide shows meanwhile: the poster (without one, the video's first
  // frame) over the small default image, so a slow or broken image never
  // leaves the slide see-through. Not both: on a slow link every image
  // competes with the video.
  const posters = useMemo(
    () =>
      showPoster
        ? [
            img || (video?.kalturaId && kalturaFirstFrameUrl(video.kalturaId)),
            playerConfig.fakeWindow.siteConfig.default_images.tv.default_image,
          ].filter(Boolean)
        : [],
    [showPoster, img, video?.kalturaId]
  )

  // Refs
  const kalturaRef = useRef(null)
  const currentMilestoneRef = useRef(0.25)
  const timerNextVideoTip = useRef<NodeJS.Timeout | null>(null)

  // The posters go once a video in the box (the clip's or an ad's) has its
  // first picture: see PlayerDiv. Media events do not bubble but pass the box
  // on their way down, so it hears 'loadeddata' as soon as the browser has the
  // frame; Kaltura passes the news on later ('canplay' up to 0.3 s), with the
  // frame already on screen. Unlike hasPicture below, a stall does not undo
  // it (the last picture stays on screen); a new player or clip starts over.
  const [pictureDrawn, setPictureDrawn] = useState(false)
  useEffect(() => {
    setPictureDrawn(false)
  }, [kp, video?.kalturaId])
  useEffect(() => {
    const box: HTMLElement | null = kalturaRef.current
    if (!box) {
      return undefined
    }
    const drawn = () => setPictureDrawn(true)
    box.addEventListener('loadeddata', drawn, true)
    return () => box.removeEventListener('loadeddata', drawn, true)
  }, [playerId])

  // Analytics(Context)
  const pageData = usePage()
  const player = usePlayer()
  // the user's own choice: a mute the browser forced is not kept, each clip asks for sound again
  const soundOff = player.isMuted && !player.muteForced
  const isPageVisible = usePageVisibility()

  const sendGA4Event = useCallback(
    (eventName: string, args: {}) => {
      // SHORTS-32: vertical only, Shorts sends stories_card_start / _reach_N
      if (
        player.componentType === 'shorts' &&
        /Video_(content_start_1010|video_reach_\d+)$/.test(eventName)
      ) {
        return
      }
      eventName = isApp && appEventPrefix ? `app_${eventName}` : eventName
      sendGA4({
        title,
        eventName: eventName as t_event_name,
        pageData: pageData,
        video_duration: video.duration,
        ReferenceId: video.name,
        house_number: video.onAirID,
        is_autoplay: 'yes', // its always autoplay
        ...analyticsParams,
        ...args,
      })
    },
    [
      player.componentType,
      isApp,
      appEventPrefix,
      pageData,
      title,
      video.duration,
      video.name,
      video.onAirID,
      analyticsParams,
    ]
  )

  // get the title header from the page data
  useEffect(() => {
    if (timelineItemTime) {
      let timelines
      if (pageData?.Content?.Item?.timeLines) {
        timelines = Object.values(pageData?.Content?.Item?.timeLines)[0]
        const text = timelines.header.title
        // const match = text.match(/יום\s*ה\s*-\s*(\d+)/)
        const match = text.match(/\d+/)
        if (match) {
          const result = `יום ${match[0]}`
          setTitleHeader(result)
        }
      } else {
        if (timelineItemDay && timelineItemDay !== '0') {
          setTitleHeader(`יום ${timelineItemDay}`)
        }
      }
    }
  }, [pageData?.Content?.Item?.timeLines, timelineItemDay, timelineItemTime])

  useEffect(() => {
    if (kp) {
      if (isPageVisible) {
        if (afterPageIsNotVisible) {
          setAfterPageIsNotVisible(false)
          if (isVisible && !wasPaused) {
            kp.paused && playQuietly(kp)
          }
        }
      } else if (!afterPageIsNotVisible) {
        const wasPaused = kp.paused
        setAfterPageIsNotVisible(true)
        kp.pause()
        setWasPaused(wasPaused)
      }
    }
  }, [kp, isPageVisible, isVisible, wasPaused, afterPageIsNotVisible])

  // the Kaltura script may still be loading when a card is tapped
  const kalturaReady = useRootStore(state => state.playerScript.kalturaReady)
  const load = shouldLoad ?? isActive
  const isActiveRef = useRef(isActive)
  useEffect(() => {
    isActiveRef.current = isActive
  }, [isActive])
  // the slide the user tapped open is not being swiped past: no setup delay
  const openedHereRef = useRef(isActive)

  useEffect(() => {
    if (kpSetKpTO.current) {
      clearTimeout(kpSetKpTO.current)
      kpSetKpTO.current = null
    }

    if (!kalturaReady || !window.KalturaPlayer || !load || !playerId) {
      safeDestroyKp(kpRef.current)
      kpRef.current = null
      setKp(null)
      return
    }

    kpSetKpTO.current = setTimeout(
      () => {
        if (!document.getElementById(playerId)) {
          return
        }
        // the active slide starts itself ('inview' as before: no autoplay
        // capability test, which costs ~0.5 s); a look-ahead slide keeps autoplay
        // off, which is also what makes Kaltura preload it
        const startsItself = isActiveRef.current
        const config = getVerticalPlayerConfig({
          playerId,
          // the slide paints the poster itself (above); given to Kaltura it is
          // downloaded a second time
          poster: '',
          loop: loop !== undefined ? loop : !player.isMiniSeries,
          allowMutedAutoPlay: true, //!player.isMiniSeries,
          adTagUrl,
          autoplay: startsItself ? 'inview' : false,
          muted: soundOff,
        })
        try {
          safeDestroyKp(kpRef.current)
          const instance = window.KalturaPlayer.setup(config)
          kpRef.current = instance
          setKp(instance)
        } catch (e) {
          prettyLog('player', { e }, 'error')
        }
      },
      isActiveRef.current && !openedHereRef.current ? ACTIVE_SETUP_DELAY_MS : 0
    )
    openedHereRef.current = false

    return () => {
      if (kpSetKpTO.current) {
        clearTimeout(kpSetKpTO.current)
        kpSetKpTO.current = null
      }
      safeDestroyKp(kpRef.current)
      kpRef.current = null
      if (navigator.mediaSession) {
        try {
          navigator.mediaSession.metadata = null
        } catch (error) {
          console.error('Error resetting media session metadata:', error)
        }
      }
    }
  }, [
    kalturaReady,
    playerId,
    img,
    video.duration,
    load,
    player.isMiniSeries,
    loop,
    adTagUrl,
  ])

  const mediaLoadRef = useRef<Promise<unknown> | null>(null)
  useEffect(() => {
    if (!kp || !video?.kalturaId) {
      return
    }
    // resume: Kaltura starts the media there itself (hls.js startPosition,
    // native after metadata); a seek before play() can be lost on Safari
    const start = startAtRef.current
    startAtRef.current = 0
    playbackStartedRef.current = false
    const load = Promise.resolve(
      kp.loadMedia(
        { entryId: video.kalturaId },
        start > 0 ? { startTime: start } : undefined
      )
    )
    // a look-ahead slide destroyed while it loads rejects with nobody waiting:
    // not an error (the active slide handles its own failure below)
    load.catch(() => undefined)
    mediaLoadRef.current = load
  }, [kp, video.kalturaId])

  /**
   * Only the active slide plays; a look-ahead slide stays loaded and paused.
   * Sound is set before play() and never turned on later from code: iOS pauses
   * a playing video that is unmuted without a tap.
   */
  useEffect(() => {
    // also for a player created for the active slide: its 'inview' autoplay
    // runs once, and a slide that comes back (swiped away and back) was paused
    if (!kp || !isActive || !mediaLoadRef.current) {
      return
    }
    let cancelled = false
    kp.muted = soundOff
    const play = () => {
      if (cancelled || !kp.paused) {
        return
      }
      if (pausedRef.current) {
        return
      }
      if (document.visibilityState === 'visible') {
        playQuietly(kp)
        return
      }
      // activated in a background tab (auto-advance): start when it is back
      document.addEventListener('visibilitychange', play, { once: true })
    }
    mediaLoadRef.current
      .catch(e => {
        // the user left the clip (its player is being destroyed): a retry
        // would start a load that Kaltura then rejects with nobody waiting
        if (cancelled || e?.code === KALTURA_DESTROYED_WHILE_LOADING) {
          throw e
        }
        // one retry after a network blip, or the slide stays on its poster
        prettyLog('player loadMedia', { e }, 'error')
        return kp.loadMedia({ entryId: video.kalturaId })
      })
      .then(play, e => {
        if (cancelled || e?.code === KALTURA_DESTROYED_WHILE_LOADING) {
          return
        }
        prettyLog('player loadMedia retry', { e }, 'error')
        onErrorRef.current?.(String(e?.message ?? e?.code ?? 'loadMedia'))
      })
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', play)
    }
    // soundOff is read once per activation on purpose (the mute button sets kp itself)
  }, [kp, isActive, video.kalturaId])

  // the slide swiped away keeps its player until the swipe ends (no poster
  // flash), but it stops playing at once
  useEffect(() => {
    if (kp && !isActive && !kp.paused) {
      kp.pause()
    }
  }, [kp, isActive])

  // the sound button may sit outside the player: follow the shared choice in
  // the layout phase, still inside the tap (iOS pauses a video unmuted later).
  // A mute the browser forced is not applied: the clip asks for sound itself
  useLayoutEffect(() => {
    if (
      kp &&
      isActiveRef.current &&
      !player.muteForced &&
      kp.muted !== player.isMuted
    ) {
      kp.muted = player.isMuted
    }
  }, [kp, player.isMuted, player.muteForced])

  // Playkit severity 2 = critical: this clip will not play
  const ERROR_HANDLER = useCallback(
    (event?: { payload?: { severity?: number; code?: number } }) => {
      if (event?.payload?.severity === 2 && isActiveRef.current) {
        prettyLog('player error', event.payload, 'error')
        onErrorRef.current?.(`playkit ${event.payload.code ?? ''}`.trim())
      }
    },
    []
  )
  useEventListener(PLAYER_EVENT.ERROR, ERROR_HANDLER, kp)

  const lastRestartRef = useRef(restartKey)
  useEffect(() => {
    if (lastRestartRef.current === restartKey) {
      return
    }
    lastRestartRef.current = restartKey
    if (kp && isActiveRef.current) {
      kp.currentTime = 0
      if (!pausedRef.current && kp.paused) {
        playQuietly(kp)
      }
    }
  }, [kp, restartKey])

  // moved from outside (the progress line): each new id moves the active clip
  const lastSeekRef = useRef(seekTo?.id)
  useEffect(() => {
    if (!seekTo || lastSeekRef.current === seekTo.id) {
      return
    }
    if (kp && isActiveRef.current) {
      lastSeekRef.current = seekTo.id
      kp.currentTime = seekTo.time
    }
  }, [kp, seekTo])

  // paused from outside (hold, tap). Only a change acts: the activation above
  // starts the clip (and seeks to startAt first), this must not race it
  const lastPausedRef = useRef(paused)
  useEffect(() => {
    if (lastPausedRef.current === paused) {
      return
    }
    lastPausedRef.current = paused
    if (!kp || !isActiveRef.current) {
      return
    }
    if (paused) {
      kp.pause()
      return
    }
    mediaLoadRef.current?.then(
      () => {
        if (isActiveRef.current && !pausedRef.current && kp.paused) {
          playQuietly(kp)
        }
      },
      () => undefined
    )
  }, [kp, paused])

  /**
   * The viewer draws its own loading frame over the clip and its controls:
   * it is told when this clip, on screen and meant to play, waits for its
   * picture (a new player, a stall). Only there is it tracked.
   */
  const tracksLoading = !!onLoading
  const onLoadingRef = useRef(onLoading)
  onLoadingRef.current = onLoading
  const [hasPicture, setHasPicture] = useState(false)
  // a new player has drawn nothing yet
  useEffect(() => {
    if (tracksLoading) {
      setHasPicture(false)
    }
  }, [kp, tracksLoading])
  const PICTURE_READY_HANDLER = useCallback(() => {
    if (tracksLoading) {
      setHasPicture(true)
    }
  }, [tracksLoading])
  const PICTURE_WAITING_HANDLER = useCallback(() => {
    if (tracksLoading) {
      setHasPicture(false)
    }
  }, [tracksLoading])
  useEventListener(PLAYER_EVENT.CAN_PLAY, PICTURE_READY_HANDLER, kp)
  useEventListener(PLAYER_EVENT.PLAYING, PICTURE_READY_HANDLER, kp)
  useEventListener(PLAYER_EVENT.Core.WAITING, PICTURE_WAITING_HANDLER, kp)
  // a stall told by the video itself: Kaltura passes its 'waiting' on about
  // 200ms later, and the wait counts from the stall. Before the first picture
  // there is nothing to stall (the video element comes with the media)
  useEffect(() => {
    const media: HTMLVideoElement | undefined =
      tracksLoading && hasPicture ? kp?.getVideoElement?.() : undefined
    if (!media) {
      return undefined
    }
    media.addEventListener('waiting', PICTURE_WAITING_HANDLER)
    return () => media.removeEventListener('waiting', PICTURE_WAITING_HANDLER)
  }, [kp, tracksLoading, hasPicture, PICTURE_WAITING_HANDLER])
  const loading = tracksLoading && isActive && !paused && !hasPicture
  // before the paint: a viewer taking over from a loading screen draws its
  // frame in the same paint, with no bright frame between the two
  useLayoutEffect(() => {
    if (!loading) {
      return undefined
    }
    onLoadingRef.current?.(true)
    return () => onLoadingRef.current?.(false)
  }, [loading])

  // iOS refuses sound without a tap: keep playing muted and show it on the button.
  // Only for a refusal on the active slide; an interrupted play() (AbortError on a
  // swipe or a pause) is not a refusal.
  const playMuted = useCallback(() => {
    if (!kp || !isActiveRef.current) {
      return
    }
    if (kp.muted) {
      // refused even without sound (iOS Low Power Mode): nothing is on its
      // way, the clip waits for a tap, so no loader is drawn over it
      if (tracksLoading) {
        setHasPicture(true)
      }
      return
    }
    kp.muted = true
    player.forceMute()
    playQuietly(kp)
  }, [kp, player, tracksLoading])
  const PLAY_FAILED_HANDLER = useCallback(
    (event?: { payload?: { name?: string; error?: { name?: string } } }) => {
      const name = event?.payload?.error?.name ?? event?.payload?.name
      if (name === 'NotAllowedError') {
        playMuted()
      }
    },
    [playMuted]
  )
  useEventListener(PLAYER_EVENT.PLAY_FAILED, PLAY_FAILED_HANDLER, kp)
  // Kaltura's own autoplay reports a refusal this way (no error object)
  useEventListener(PLAYER_EVENT.AUTOPLAY_FAILED, playMuted, kp)
  useEventListener(
    PLAYER_EVENT.FALLBACK_TO_MUTED_AUTOPLAY,
    player.forceMute,
    kp
  )
  // sound is really playing: the mute the browser forced is over
  const SOUND_PLAYING_HANDLER = useCallback(() => {
    if (kp && isActiveRef.current && !kp.muted && player.muteForced) {
      player.releaseMute()
    }
  }, [kp, player])
  useEventListener(PLAYER_EVENT.PLAYING, SOUND_PLAYING_HANDLER, kp)

  useEffect(() => {
    if (kp) {
      if (!isVisible) {
        if (keepPlace) {
          // kept for a reopening: it stays on its second, only paused. A
          // reopening is a new viewing: Kaltura sends PLAYBACK_START once per
          // load, so its 'playing' sends the content start
          hasEndedRef.current =
            hasEndedRef.current || playbackStartedRef.current
          kp.pause()
          return
        }
        // a played clip kept next to the active slide starts over when the
        // user comes back: Kaltura sends PLAYBACK_START once per load, so the
        // next 'playing' sends the content start instead (as after an end)
        hasEndedRef.current = hasEndedRef.current || kp.currentTime > 0
        kp.currentTime = 0 // Rewind the video
        currentMilestoneRef.current = 0.25
        setProgressReportMap([...default_player_progress_report_map])
        kp.pause() // Pause the video
      }
    }
  }, [isVisible, kp, title, img, keepPlace])

  useEffect(() => {
    if (firstPlay) {
      setIsNextVideoTip(true)
      timerNextVideoTip.current = setTimeout(() => {
        setIsNextVideoTip(false)
      }, 7000)
    }

    return () => {
      if (timerNextVideoTip.current) {
        clearTimeout(timerNextVideoTip.current)
      }
    }
  }, [firstPlay])

  /**
   * Analytics
   */
  const PLAYBACK_START_HANDLER = useCallback(() => {
    playbackStartedRef.current = true
    sendGA4Event(`${eventPrefix}Video_content_start_1010`, {})
  }, [sendGA4Event, eventPrefix])

  useEventListener(PLAYER_EVENT.PLAYBACK_START, PLAYBACK_START_HANDLER, kp)

  const PROGRESS_HANDLER = useCallback(() => {
    const currentProgress = kp?.currentTime / video.duration
    if (
      progress_report_map.length > 0 &&
      currentProgress >= currentMilestoneRef.current
    ) {
      sendGA4Event(
        `${eventPrefix}Video_video_reach_${progress_report_map[0] * 100}`,
        {}
      )
      setProgressReportMap(prev => {
        const newMap = prev.slice(1)
        currentMilestoneRef.current = newMap[0] || 1
        return newMap
      })
    }
  }, [
    kp?.currentTime,
    video.duration,
    progress_report_map,
    sendGA4Event,
    eventPrefix,
  ])
  useEventListener(PLAYER_EVENT.TIME_UPDATE, PROGRESS_HANDLER, kp)

  // onTime: the clip's time read on a steady beat while it plays, not on
  // timeupdate, which some browsers fire only about once a second
  useEffect(() => {
    if (!kp || !isActive || !onTimeRef.current) {
      return undefined
    }
    const timer = setInterval(() => {
      // before Kaltura has its media engine both read undefined: no report
      const time = kp.currentTime
      if (kp.paused === false && Number.isFinite(time)) {
        onTimeRef.current?.(time, kp.duration)
      }
    }, TIME_REPORT_MS)
    return () => clearInterval(timer)
  }, [kp, isActive])

  const PLAYBACK_ENDED_HANDLER = useCallback(() => {
    if (kp) {
      sendGA4Event(`${eventPrefix}Video_video_reach_100`, {})
      hasEndedRef.current = true
      currentMilestoneRef.current = 0.25
      setProgressReportMap([...default_player_progress_report_map])
      if (onEndedRef.current) {
        onEndedRef.current()
      } else if (player.isMiniSeries) {
        if (!swiper.isEnd) {
          swiper.slideNext()
        } else {
          // Jump straight to the last slide index
          swiper.slideTo(0)
        }
      }
    }
  }, [kp, sendGA4Event, eventPrefix, player.isMiniSeries, swiper])

  useEventListener(PLAYER_EVENT.PLAYBACK_ENDED, PLAYBACK_ENDED_HANDLER, kp)

  // tells the feed this clip is playing with a cushion, once per activation
  const reportedRef = useRef(false)
  const startedRef = useRef(false)
  const onStartRef = useRef(onStart)
  onStartRef.current = onStart
  useEffect(() => {
    if (!isActive) {
      reportedRef.current = false
      startedRef.current = false
    }
  }, [isActive])
  const reportFirstPicture = useCallback(() => {
    if (isActive && !startedRef.current) {
      startedRef.current = true
      onStartRef.current?.(index)
    }
  }, [isActive, index])
  const reportStarted = useCallback(() => {
    if (!isActive || reportedRef.current || !kp) {
      return
    }
    const media: HTMLVideoElement | undefined = kp.getVideoElement?.()
    const ranges = media?.buffered
    // native HLS (Safari) may report no ranges at all while playing: then
    // there is nothing to wait for
    if (media && ranges && ranges.length) {
      const end = ranges.end(ranges.length - 1)
      const toEnd = Number.isFinite(media.duration) ? media.duration - end : 1
      if (end - media.currentTime < NEIGHBOURS_AFTER_BUFFER_S && toEnd > 0.5) {
        return
      }
    }
    reportedRef.current = true
    onPlaying?.(index)
  }, [isActive, kp, onPlaying, index])

  const PLAYING_HANDLER = useCallback(() => {
    if (kp) {
      if (navigator.mediaSession) {
        try {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: title,
            artist: 'רשת 13',
            album: 'reels13',
            artwork: [{ src: img ?? '' }],
          })
        } catch (error) {
          console.error('Error setting media session metadata:', error)
        }
      }
    }
    if (hasEndedRef.current) {
      hasEndedRef.current = false
      sendGA4Event(`${eventPrefix}Video_content_start_1010`, {})
    }
    reportFirstPicture()
    reportStarted()
  }, [
    kp,
    title,
    img,
    sendGA4Event,
    eventPrefix,
    reportFirstPicture,
    reportStarted,
  ])
  useEventListener(PLAYER_EVENT.PLAYING, PLAYING_HANDLER, kp)
  const TIME_UPDATE_HANDLER = useCallback(() => {
    // a time that moves while it plays: also a start the 'playing' event missed
    if (kp && !kp.paused) {
      reportFirstPicture()
    }
    reportStarted()
  }, [kp, reportFirstPicture, reportStarted])
  useEventListener(PLAYER_EVENT.TIME_UPDATE, TIME_UPDATE_HANDLER, kp)

  const FIRST_PLAY_HANDLER = useCallback(() => {
    if (kp) {
      if (index === 0) {
        setFirstPlay(true)
      }
    }
  }, [kp, index])
  useEventListener(PLAYER_EVENT.FIRST_PLAY, FIRST_PLAY_HANDLER, kp)

  const handleMute = useCallback(() => {
    if (kp) {
      kp.muted = !player.isMuted
      player.toggleMute()
    }
  }, [kp, player])

  /**
   * RESH13-1138 - play state is needed only where we draw our own play button
   * (the item page). Everywhere else - reels, timelines, big brother - these
   * handlers do nothing, so no extra renders are added to the feed.
   */
  const tracksPlayState = !!playerStyle?.showCenterPlayButton

  const PLAY_STATE_PLAY_HANDLER = useCallback(() => {
    if (tracksPlayState) {
      setIsPaused(false)
    }
  }, [tracksPlayState])
  useEventListener(PLAYER_EVENT.PLAY, PLAY_STATE_PLAY_HANDLER, kp)

  const PLAY_STATE_PAUSE_HANDLER = useCallback(() => {
    if (tracksPlayState) {
      setIsPaused(true)
    }
  }, [tracksPlayState])
  useEventListener(PLAYER_EVENT.PAUSE, PLAY_STATE_PAUSE_HANDLER, kp)

  /* the content stays paused while an ad plays - the ad has its own controls */
  const AD_BREAK_START_HANDLER = useCallback(() => {
    if (tracksPlayState) {
      setIsAdPlaying(true)
    }
  }, [tracksPlayState])
  useEventListener(PLAYER_EVENT.AD_BREAK_START, AD_BREAK_START_HANDLER, kp)

  const AD_BREAK_END_HANDLER = useCallback(() => {
    if (tracksPlayState) {
      setIsAdPlaying(false)
    }
  }, [tracksPlayState])
  useEventListener(PLAYER_EVENT.AD_BREAK_END, AD_BREAK_END_HANDLER, kp)
  /* an ad error does not always end the break - do not leave the UI stuck */
  useEventListener(PLAYER_EVENT.AD_ERROR, AD_BREAK_END_HANDLER, kp)

  const handleTogglePlay = useCallback(() => {
    if (!kp) {
      return
    }
    if (kp.paused) {
      playQuietly(kp)
    } else {
      kp.pause()
    }
  }, [kp])

  const handlePressPlayerLink = useCallback(
    (full_story_url: string) => {
      handleClosePlayer()
      sendGA4Event('click_full_story', { full_story_url })
    },
    [handleClosePlayer, sendGA4Event]
  )

  /**
   * RESH13-1138 - "to the next video" only means something inside a feed. The
   * item page mounts this player on its own, without a swiper, and there is
   * nothing to swipe to, so the hint must not appear there.
   */
  const showNextVideoTip = !!swiper && isNextVideoTip

  return (
    <PlayerDiv
      key={playerId}
      id={playerId}
      className={playerId}
      ref={kalturaRef}
      showNextVideoTip={index === 0 && showNextVideoTip}
      $lookAhead={!isActive}
      seekBarColor={seekBarColor}
      playerStyle={playerStyle}
      posters={posters}
      data-drawn={pictureDrawn || undefined}
    >
      {/* the active slide (its X shows before the player is ready) and a slide
          that still has a player (swiped away, mid-transition); not every
          slide of the feed */}
      {!hideOverlay && (isActive || !!kp) && (
        <PlayerOverlay
          index={index || 0}
          realIndex={realIndex}
          handleMute={handleMute}
          isPaused={isPaused && !isAdPlaying && !!kp}
          isAdPlaying={isAdPlaying}
          handleTogglePlay={handleTogglePlay}
          playerStyle={playerStyle}
          isNextVideoTip={showNextVideoTip}
          handleClosePlayer={handleClosePlayer}
          timelineItemTime={timelineItemTime}
          titleHeader={titleHeader}
          episode={episode}
          link={link}
          link_title={link_title}
          handlePressPlayerLink={() => link && handlePressPlayerLink(link)}
          title={title}
        />
      )}
    </PlayerDiv>
  )
}
